import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import styled, { keyframes } from 'styled-components'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded'
import FolderOpenRoundedIcon from '@mui/icons-material/FolderOpenRounded'
import { CircularProgress } from '@mui/material'
import { toast } from 'react-toastify'
import { fetchReportEvidence, logPublicSourceView } from 'services/reportingService'

const ReportEvidencePage = () => {
  const { token } = useParams()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [files, setFiles] = useState([])
  const [meta, setMeta] = useState({})
  const [openingId, setOpeningId] = useState(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    fetchReportEvidence(token)
      .then(res => {
        if (cancelled) return
        setFiles(res?.data?.data || [])
        setMeta(res?.data?.meta || {})
        setError(null)
      })
      .catch(() => {
        if (!cancelled) setError('This link is invalid or has expired.')
      })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [token])

  const openFile = async (file) => {
    if (openingId) return
    setOpeningId(file.id)
    try {
      const res = await logPublicSourceView(token, file.id)
      const url = res?.data?.data?.url
      if (url) {
        window.open(url, '_blank', 'noopener,noreferrer')
      } else {
        toast.error('Could not open this document.')
      }
    } catch (e) {
      toast.error('Could not open this document.')
    } finally {
      setOpeningId(null)
    }
  }

  const formatDate = (iso) => {
    if (!iso) return ''
    try { return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) }
    catch (e) { return '' }
  }

  return (
    <Page>
      <Card>
        <Header>
          <IconTile><FolderOpenRoundedIcon /></IconTile>
          <HeaderText>
            <Eyebrow>Referenced evidence</Eyebrow>
            <Title>{meta.reportName || 'Report'}</Title>
            {meta.buildingName && <Sub>{meta.buildingName}{meta.version ? ` · v${meta.version}` : ''}</Sub>}
          </HeaderText>
        </Header>

        <Intro>
          These are the documents cited as evidence in the report. Open any document to view or download it.
        </Intro>

        {loading ? (
          <List>
            {[0, 1, 2, 3, 4].map(i => (
              <SkeletonRow key={i}>
                <SkeletonTile />
                <SkeletonLines>
                  <SkeletonLine style={{ width: '58%' }} />
                  <SkeletonLine style={{ width: '32%' }} />
                </SkeletonLines>
              </SkeletonRow>
            ))}
          </List>
        ) : error ? (
          <EmptyState>{error}</EmptyState>
        ) : files.length === 0 ? (
          <EmptyState>No evidence documents were referenced in this report.</EmptyState>
        ) : (
          <>
            <CountLine>{files.length} document{files.length === 1 ? '' : 's'}</CountLine>
            <List>
              {files.map(file => (
                <Row key={file.id} type="button" onClick={() => openFile(file)} disabled={openingId === file.id}>
                  <FileTile><DescriptionOutlinedIcon /></FileTile>
                  <FileMeta>
                    <FileName>{file.displayName || 'Document'}</FileName>
                    <FileSub>
                      {file.fileType || 'Document'}{file.createdAt ? ` · ${formatDate(file.createdAt)}` : ''}
                    </FileSub>
                  </FileMeta>
                  {openingId === file.id
                    ? <CircularProgress size={16} sx={{ color: '#33becc' }} />
                    : <OpenInNewRoundedIcon style={{ fontSize: 18, color: '#9aa0ab' }} />}
                </Row>
              ))}
            </List>
          </>
        )}

        <Footer>Shared securely via Building Passport.</Footer>
      </Card>
    </Page>
  )
}

export default ReportEvidencePage

const pulse = keyframes`
  0% { opacity: 0.55; }
  50% { opacity: 1; }
  100% { opacity: 0.55; }
`

const Page = styled.div`
  min-height: 100vh;
  background: #f5f6f8;
  display: flex;
  justify-content: center;
  padding: 40px 16px;
`

const Card = styled.div`
  width: 100%;
  max-width: 720px;
  background: #fff;
  border: 1px solid #ececec;
  border-radius: 16px;
  padding: 28px;
  box-shadow: 0 6px 24px rgba(12, 18, 41, 0.06);
`

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
`

const IconTile = styled.div`
  width: 44px; height: 44px;
  border-radius: 12px;
  background: #eef2ff;
  color: #4f46e5;
  display: inline-flex; align-items: center; justify-content: center;
  flex-shrink: 0;
  svg { font-size: 24px; }
`

const HeaderText = styled.div` min-width: 0; `

const Eyebrow = styled.div`
  font-size: 11px; font-weight: 800; letter-spacing: 0.05em; text-transform: uppercase;
  color: #6b7280;
`

const Title = styled.h1`
  margin: 2px 0 0;
  font-size: 20px; font-weight: 700; color: #0c1229;
`

const Sub = styled.div`
  font-size: 13px; color: #6b7280; margin-top: 2px;
`

const Intro = styled.p`
  margin: 18px 0 8px;
  font-size: 13.5px; line-height: 1.6; color: #4d4d56;
`

const CountLine = styled.div`
  font-size: 12px; font-weight: 700; color: #6b7280;
  margin: 10px 2px 6px;
`

const List = styled.div`
  display: flex; flex-direction: column; gap: 8px; margin-top: 8px;
`

const Row = styled.button`
  font: inherit;
  display: flex; align-items: center; gap: 12px;
  padding: 12px 14px;
  background: #fff;
  border: 1px solid #ececec;
  border-radius: 12px;
  cursor: pointer;
  text-align: left;
  transition: border-color 0.15s ease, background 0.15s ease;
  &:hover:not(:disabled) { border-color: #d3d7e0; background: #fafbfc; }
  &:disabled { cursor: default; opacity: 0.7; }
`

const FileTile = styled.span`
  width: 36px; height: 36px;
  border-radius: 9px;
  background: #f1f5f9;
  color: #475569;
  display: inline-flex; align-items: center; justify-content: center;
  flex-shrink: 0;
  svg { font-size: 20px; }
`

const FileMeta = styled.div`
  display: flex; flex-direction: column; min-width: 0; flex: 1;
`

const FileName = styled.span`
  font-size: 14px; font-weight: 600; color: #0c1229;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
`

const FileSub = styled.span`
  font-size: 12px; color: #6b7280; margin-top: 1px;
`

const EmptyState = styled.div`
  margin-top: 16px;
  padding: 28px;
  text-align: center;
  font-size: 13.5px; color: #6b7280;
  background: #fafbfc;
  border: 1px dashed #e2e5eb;
  border-radius: 12px;
`

const Footer = styled.div`
  margin-top: 22px;
  font-size: 11.5px; color: #9aa0ab; text-align: center;
`

const SkeletonRow = styled.div`
  display: flex; align-items: center; gap: 12px;
  padding: 12px 14px;
  border: 1px solid #f0f0f2;
  border-radius: 12px;
`

const SkeletonTile = styled.span`
  width: 36px; height: 36px; border-radius: 9px;
  background: #e9ebef; flex-shrink: 0;
  animation: ${pulse} 1.4s ease-in-out infinite;
`

const SkeletonLines = styled.div`
  display: flex; flex-direction: column; gap: 8px; flex: 1;
`

const SkeletonLine = styled.span`
  height: 10px; border-radius: 5px; background: #e9ebef;
  animation: ${pulse} 1.4s ease-in-out infinite;
`
